import { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useUser } from '../../../hooks/useUser';
import { useAgency } from '../../../hooks/useAgency';
import { updateOfficialImage, OfficialData } from '../../../networkRequest/officialsService';
import { validateFile } from '../../../utils/dropzoneImageValidator';
import Utils from '../../../utils/utils';
import { COPY } from '../../../constants';
import { ImageSelector, UserAvatar } from '@components';

interface UserPhotoPanelProps {
  official: OfficialData;
  setData: (OfficialDetailData) => void;
  setError: (string) => void;
}

const UserPhotoPanel = ({ official, setData, setError }: UserPhotoPanelProps) => {
  const { user } = useUser();
  const { agency } = useAgency();
  const [imgUploading, setImgUploading] = useState(false);

  const onDropAccepted = useCallback(
    async (file) => {
      if (user && agency) {
        setImgUploading(true);
        const { data, error } = await updateOfficialImage(
          user.accessToken,
          official.id,
          agency.id,
          file[0]
        );
        if (data) {
          setData(data);
        } else if (error) {
          console.error(error);
          setError(error);
        }
        setImgUploading(false);
      }
    },
    [official.id, setData, setError, user, agency]
  );

  const imageTooltipContent = (
    <ul>
      <li>{COPY.imageUpload.step1}</li>
      <li>{COPY.imageUpload.step2}</li>
      <li>{COPY.imageUpload.step3}</li>
      <li>{COPY.imageUpload.fileType}</li>
    </ul>
  );

  const validateOfficialImage = validateFile(3, [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/heic'
  ]);

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDropAccepted,
    maxFiles: 1,
    validator: validateOfficialImage
  });

  useEffect(() => {
    if (fileRejections.length > 0) {
      let error = fileRejections[0].errors[0].message;
      if (error.includes('Unsuported file') || error.includes('File too large')) {
        error += `. ${COPY.imageUpload.fileType}`;
      }
      setError(error);
    }
  }, [fileRejections, setError]);

  return (
    <Box
      {...getRootProps()}
      sx={{
        width: '100%',
        maxWidth: 260
      }}>
      <Box
        sx={{
          width: '100%',
          aspectRatio: '1 / 1',
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {imgUploading
          ? <CircularProgress size='20%' />
          : (
            <UserAvatar
              src={official.img_url}
              alt={Utils.fullName(official.first_name, official.last_name)}
            />
          )
        }
      </Box>
      {/* Rendered here rather than via ImageSelector's `explanation` so it sits above the button */}
      <Typography
        variant='bodySmall'
        sx={{
          display: 'block',
          mb: 1
        }}>
        SurePass requires a recent photo for in-person identity verification.
      </Typography>
      <ImageSelector
        dropzoneInputProps={getInputProps()}
        tooltipContent={imageTooltipContent}
        disabled={imgUploading}
      />
    </Box>
  );
};

export default UserPhotoPanel;
